import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle } from 'lucide-react';
import { LEGAL_LINKS } from '@/constants/legal';
import { useLocation } from 'wouter';

// The documents this notice asks people to read must stay readable before they accept it.
const EXEMPT_ROUTES: readonly string[] = LEGAL_LINKS.map((l) => l.href);

const STORAGE_KEY = 'disclaimer-accepted';
// Bumped with the notice's wording (legal pack S2 item (o)) so everyone sees the new version once.
// The key keeps its name: the Cookie Policy lists it.
const ACCEPTED_VERSION = 'v2';

export const DisclaimerDialog = () => {
  const [open, setOpen] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [location] = useLocation();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) !== ACCEPTED_VERSION) {
      setOpen(true);
    }
  }, []);

  const handleContinue = () => {
    localStorage.setItem(STORAGE_KEY, ACCEPTED_VERSION);
    setOpen(false);
  };

  return (
    <Dialog open={open && !EXEMPT_ROUTES.includes(location)} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-lg [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <DialogTitle className="text-xl font-bold">Important notice</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
            <p className="text-sm text-foreground leading-relaxed">
              AgentCouncil gives general information generated by AI. It is not financial advice or a personal
              recommendation, and it does not know your circumstances. AI outputs can be wrong. AgentCouncil is not a
              trading platform: you cannot buy, sell or trade any asset here. Aspis Protocol Ltd, which runs AgentCouncil,
              is not authorised or regulated by the Financial Conduct Authority. Cryptoassets are high-risk. Their value
              can fall to zero, and you could lose all the money you put into them.
            </p>
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="disclaimer-accept"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="disclaimer-accept" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
              I agree to the{' '}
              <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-primary underline" onClick={(e) => e.stopPropagation()}>Terms of Use</a>, and I have read the{' '}
              <a href="/disclaimer" target="_blank" rel="noopener noreferrer" className="text-primary underline" onClick={(e) => e.stopPropagation()}>Disclaimer</a> and{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
            </label>
          </div>
        </div>

        <div>
          <Button onClick={handleContinue} disabled={!accepted} className="w-full mt-2">
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
